/**
 * Tiny cookie helper used by the "remember last viewed model" feature.
 *
 * Implementation notes:
 *   - We deliberately keep this dependency-free. `document.cookie` is the
 *     simplest correct way to read/write a cookie that survives across tabs,
 *     PWA installs, and full app reloads.
 *   - Values are JSON-encoded then URI-encoded so we can safely round-trip
 *     structured payloads ({ brandKey, year, model }) without worrying about
 *     reserved cookie characters.
 *   - All cookies are scoped to `Path=/` so they're shared across every brand
 *     sub-path (`/forestriverrv`, `/coachmenrv`, …) and the optional editor
 *     paths.
 *   - `SameSite=Lax` is the safest default for first-party navigation cookies
 *     and avoids browser warnings under Chrome's incremental cookie tightening.
 *   - We add `Secure` only when running over https:// so local dev (http://localhost)
 *     keeps working — browsers reject `Secure` cookies on insecure origins.
 */

const LAST_VEHICLE_COOKIE = 'ok.lastVehicle'
const DEFAULT_TTL_DAYS = 90

const isBrowser = typeof document !== 'undefined' && typeof window !== 'undefined'

const isSecureContext = () =>
  isBrowser && window.location && window.location.protocol === 'https:'

export function setCookie(name, value, days = DEFAULT_TTL_DAYS) {
  if (!isBrowser) return
  try {
    const maxAge = Math.max(0, Math.floor(days * 24 * 60 * 60))
    const encoded = encodeURIComponent(value)
    const parts = [
      `${name}=${encoded}`,
      `Path=/`,
      `Max-Age=${maxAge}`,
      `SameSite=Lax`,
    ]
    if (isSecureContext()) parts.push('Secure')
    document.cookie = parts.join('; ')
  } catch (e) {
    // Some embedded webviews / privacy modes throw on write — fail open.
  }
}

export function getCookie(name) {
  if (!isBrowser || !document.cookie) return null
  const target = `${name}=`
  const all = document.cookie.split(';')
  for (let i = 0; i < all.length; i++) {
    const c = all[i].trim()
    if (c.indexOf(target) === 0) {
      try {
        return decodeURIComponent(c.substring(target.length))
      } catch (e) {
        return null
      }
    }
  }
  return null
}

export function removeCookie(name) {
  if (!isBrowser) return
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`
}

// ---------------------------------------------------------------------------
// "Last viewed vehicle" — typed wrappers around the generic cookie helpers
// ---------------------------------------------------------------------------

/**
 * Persist the last vehicle the user opened. Called from Guide.js after a
 * successful content load so we only remember vehicles the user actually
 * reached (vs. ones they merely clicked but errored out on).
 *
 * @param {{ brandKey: string, year: string, model: string }} vehicle
 */
export function saveLastVehicle({ brandKey, year, model } = {}) {
  if (!brandKey || !year || !model) return
  const payload = { brandKey, year, model, savedAt: Date.now() }
  setCookie(LAST_VEHICLE_COOKIE, JSON.stringify(payload))
}

/**
 * Read the last vehicle from the cookie. Returns `null` if missing or
 * malformed (forward-compat: a future schema change can simply bump the
 * shape and old cookies will be ignored).
 *
 * @returns {{ brandKey: string, year: string, model: string } | null}
 */
export function loadLastVehicle() {
  const raw = getCookie(LAST_VEHICLE_COOKIE)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.brandKey === 'string' &&
      typeof parsed.year === 'string' &&
      typeof parsed.model === 'string'
    ) {
      return { brandKey: parsed.brandKey, year: parsed.year, model: parsed.model }
    }
    return null
  } catch (e) {
    return null
  }
}

export function clearLastVehicle() {
  removeCookie(LAST_VEHICLE_COOKIE)
}
